import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Container, Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import cx from 'classnames';

import { setCategoryId, getProductsRequest } from '../../actions/products'
import { loadProductCatalogIfNeeded } from '../../actions/productCatalog';
import { getMerchantId } from '../../utils/merchant';
import { buildExportData, downloadJSON, validateImportData, importProductData, downloadExcel, parseExcelImport } from '../../utils/productExportImport';

import ProdList from './ProdList/ProdList';
import Categories from './Categories/Categories';
import ProductEdit from './ProductEdit/ProductEdit';
import Menus from './Menus/Menus';
import MenuDetail from './Menus/MenuDetail';

import Loader from '../../components/Loader/Loader';
import { withTranslation } from '../../i18n/LanguageContext';
import s from './Products.module.scss';

class Products extends React.Component {

    static propTypes = {
        dispatch: PropTypes.func.isRequired,
    };

    state = {
        isImporting: false,
        importProgress: { step: 0, total: 0, message: '' },
        importConfirm: null,
        showExportModal: false,
        showImportModal: false,
    };

    constructor() {
        super();
        this.jsonFileInputRef = React.createRef();
        this.excelFileInputRef = React.createRef();
    }

    loadCatalog = () => {
        this.props.dispatch(loadProductCatalogIfNeeded());
    }

    componentDidMount() {
        this.loadCatalog();
    }

    componentDidUpdate(prevProps) {
        if (getMerchantId(prevProps.merchant) !== getMerchantId(this.props.merchant)) {
            this.loadCatalog();
        }
    }

    onClickProducts = (e) => {
        this.props.dispatch(setCategoryId(0));
    }

    onClickCategories = (e) =>  {
        this.props.dispatch(setCategoryId(0));
    }

    // --- Export / Import handlers ---

    handleExportClick = () => this.setState({ showExportModal: true });
    handleExportCancel = () => this.setState({ showExportModal: false });

    handleImportClick = () => this.setState({ showImportModal: true });
    handleImportFormatCancel = () => this.setState({ showImportModal: false });

    handleExportJSON = () => {
        const { products, categories } = this.props;
        const exportData = buildExportData(products, categories);
        const dateStr = new Date().toISOString().slice(0, 10);
        downloadJSON(exportData, `products-export-${dateStr}.json`);
        this.setState({ showExportModal: false });
        toast.success(this.props.t('toast.exportedJson', { products: products.length, categories: categories.length }));
    }

    handleExportExcel = () => {
        const { products, categories } = this.props;
        const dateStr = new Date().toISOString().slice(0, 10);
        downloadExcel(products, categories, `products-export-${dateStr}.xlsx`, this.props.t);
        this.setState({ showExportModal: false });
        toast.success(this.props.t('toast.exportedExcel', { products: products.length, categories: categories.length }));
    }

    handleImportJSONClick = () => {
        this.setState({ showImportModal: false });
        if (this.jsonFileInputRef.current) {
            this.jsonFileInputRef.current.value = '';
            this.jsonFileInputRef.current.click();
        }
    }

    handleImportExcelClick = () => {
        this.setState({ showImportModal: false });
        if (this.excelFileInputRef.current) {
            this.excelFileInputRef.current.value = '';
            this.excelFileInputRef.current.click();
        }
    }

    handleJSONFileSelected = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = JSON.parse(evt.target.result);
                const validation = validateImportData(data);
                if (!validation.valid) {
                    toast.error(this.props.t('toast.invalidFile', { errors: validation.errors.join(', ') }));
                    return;
                }
                this.setState({ importConfirm: data });
            } catch (err) {
                toast.error(this.props.t('toast.parseJson'));
            }
        };
        reader.readAsText(file);
    }

    handleExcelFileSelected = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = parseExcelImport(evt.target.result);
                const validation = validateImportData(data);
                if (!validation.valid) {
                    toast.error(this.props.t('toast.invalidFile', { errors: validation.errors.join(', ') }));
                    return;
                }
                this.setState({ importConfirm: data });
            } catch (err) {
                toast.error(this.props.t('toast.parseExcel', { message: err.message }));
            }
        };
        reader.readAsArrayBuffer(file);
    }

    handleImportConfirm = async () => {
        const { importConfirm } = this.state;
        const { merchant, categories } = this.props;

        this.setState({
            importConfirm: null,
            isImporting: true,
            importProgress: { step: 0, total: 0, message: this.props.t('toast.importStarting') },
        });

        try {
            const result = await importProductData(
                importConfirm,
                merchant.MerchantId,
                categories,
                (progress) => this.setState({ importProgress: progress })
            );

            this.props.dispatch(getProductsRequest(merchant.MerchantId));

            this.setState({ isImporting: false });

            const t = this.props.t;
            const summary = [
                t('toast.catsCreated', { count: result.categoriesCreated }),
                result.categoriesSkipped ? t('toast.catsExisted', { count: result.categoriesSkipped }) : null,
                t('toast.prodsCreated', { count: result.productsCreated }),
                result.variationsCreated ? t('toast.varsCreated', { count: result.variationsCreated }) : null,
                result.optionsCreated ? t('toast.optsCreated', { count: result.optionsCreated }) : null,
                result.imagesFailed ? t('toast.imagesFailed', { count: result.imagesFailed }) : null,
            ].filter(Boolean).join(', ');

            if (result.errors.length > 0) {
                toast.warn(t('toast.importIssues', { summary, errors: result.errors.length }));
                console.warn('Import errors:', result.errors);
            } else {
                toast.success(t('toast.importComplete', { summary }));
            }
        } catch (err) {
            this.setState({ isImporting: false });
            toast.error(this.props.t('toast.importFailed', { message: err.message }));
        }
    }

    handleImportCancel = () => {
        this.setState({ importConfirm: null });
    }

    render() {
        const { pathname } = this.props.location;

        const {isReceiving, isUpdating, isDeleting, t} = this.props;
        const { isImporting, importProgress, importConfirm, showExportModal, showImportModal } = this.state;
        const progressPercent = importProgress.total > 0 ? Math.round((importProgress.step / importProgress.total) * 100) : 0;
        
        return (
            <div className="page-wrapper">
                {
                    !getMerchantId(this.props.merchant) && !this.props.isFetching && (
                        <Alert className="alert-sm page-alert" color="warning">
                            {t('productsTabs.merchantUnavailable')}
                        </Alert>
                    )
                }
                {
                    (this.props.errorMessage || this.props.catalogError) && (
                        <Alert className="alert-sm page-alert" color="danger">
                            {this.props.catalogError || this.props.errorMessage}
                        </Alert>
                    )
                }
                <div className="page-tabs">
                    <Link 
                        className={`page-tab ${pathname.startsWith('/admin/products/categories') ? 'page-tab--active' : ''}`} 
                        to="/admin/products/categories" 
                        onClick={this.onClickCategories}
                    >
                        <i className="zmdi zmdi-label"></i>
                        <span>{t('productsTabs.categories')}</span>
                    </Link>
                    <Link 
                        className={`page-tab ${pathname.startsWith('/admin/products/prod_list') ? 'page-tab--active' : ''}`} 
                        to="/admin/products/prod_list" 
                        onClick={this.onClickProducts}
                    >
                        <i className="zmdi zmdi-cutlery"></i>
                        <span>{t('productsTabs.products')}</span>
                    </Link>
                    <Link
                        className={`page-tab ${pathname.startsWith('/admin/products/menus') ? 'page-tab--active' : ''}`}
                        to="/admin/products/menus"
                    >
                        <i className="zmdi zmdi-collection-text"></i>
                        <span>{t('productsTabs.menus')}</span>
                    </Link>
                    <div className={s.importExportGroup}>
                        <button type="button" className={cx('btn', s.exportBtn)} onClick={this.handleExportClick} title={t('productsTabs.exportTitle')}>
                            <i className="zmdi zmdi-download"></i>
                            <span>{t('productsTabs.export')}</span>
                        </button>
                        <button type="button" className={cx('btn', s.importBtn)} onClick={this.handleImportClick} title={t('productsTabs.importTitle')}>
                            <i className="zmdi zmdi-upload"></i>
                            <span>{t('productsTabs.import')}</span>
                        </button>
                        <input
                            type="file"
                            ref={this.jsonFileInputRef}
                            accept=".json"
                            style={{ display: 'none' }}
                            onChange={this.handleJSONFileSelected}
                        />
                        <input
                            type="file"
                            ref={this.excelFileInputRef}
                            accept=".xlsx,.xls"
                            style={{ display: 'none' }}
                            onChange={this.handleExcelFileSelected}
                        />
                    </div>
                </div>

                {/* Export format modal */}
                <Modal isOpen={showExportModal} toggle={this.handleExportCancel}>
                    <ModalHeader toggle={this.handleExportCancel} style={{ background: '#1e1e2e', color: '#fff', borderColor: 'rgba(255,255,255,0.08)' }}>{t('productsTabs.exportFormat')}</ModalHeader>
                    <ModalBody style={{ background: '#1e1e2e', color: '#e0e0e0' }}>
                        <p>{t('productsTabs.exportChoose')}</p>
                        <div className="d-flex flex-column gap-2">
                            <Button color="primary" block onClick={this.handleExportExcel} className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                                <i className="zmdi zmdi-grid"></i> {t('productsTabs.excel')}
                            </Button>
                            <Button color="secondary" block onClick={this.handleExportJSON} className="d-flex align-items-center justify-content-center mt-2" style={{ gap: '8px' }}>
                                <i className="zmdi zmdi-code"></i> {t('productsTabs.json')}
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

                {/* Import format modal */}
                <Modal isOpen={showImportModal} toggle={this.handleImportFormatCancel}>
                    <ModalHeader toggle={this.handleImportFormatCancel} style={{ background: '#1e1e2e', color: '#fff', borderColor: 'rgba(255,255,255,0.08)' }}>{t('productsTabs.importFormat')}</ModalHeader>
                    <ModalBody style={{ background: '#1e1e2e', color: '#e0e0e0' }}>
                        <p>{t('productsTabs.importChoose')}</p>
                        <div className="d-flex flex-column gap-2">
                            <Button color="primary" block onClick={this.handleImportExcelClick} className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                                <i className="zmdi zmdi-grid"></i> {t('productsTabs.excel')}
                            </Button>
                            <Button color="secondary" block onClick={this.handleImportJSONClick} className="d-flex align-items-center justify-content-center mt-2" style={{ gap: '8px' }}>
                                <i className="zmdi zmdi-code"></i> {t('productsTabs.json')}
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

                {/* Import confirmation modal */}
                <Modal isOpen={!!importConfirm} toggle={this.handleImportCancel}>
                    <ModalHeader toggle={this.handleImportCancel}>{t('productsTabs.confirmImport')}</ModalHeader>
                    <ModalBody>
                        {importConfirm && (
                            <div>
                                <p>{t('productsTabs.aboutToImport')}</p>
                                <ul>
                                    <li>{t('productsTabs.categoriesCount', { count: importConfirm.categories.length })}</li>
                                    <li>{t('productsTabs.productsCount', { count: importConfirm.products.length })}</li>
                                    <li>{t('productsTabs.variationsCount', { count: importConfirm.products.reduce((sum, p) => sum + (p.Variations?.length || 0), 0) })}</li>
                                    <li>{t('productsTabs.optionsCount', { count: importConfirm.products.reduce((sum, p) => sum + (p.Options?.length || 0), 0) })}</li>
                                </ul>
                                <p className="text-muted">{t('productsTabs.importNote')}</p>
                                {importConfirm.exportDate && <p className="text-muted">{t('productsTabs.exportedOn', { date: new Date(importConfirm.exportDate).toLocaleDateString() })}</p>}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={this.handleImportCancel}>{t('common.cancel')}</Button>
                        <Button color="primary" onClick={this.handleImportConfirm}>{t('productsTabs.import')}</Button>
                    </ModalFooter>
                </Modal>

                {/* Import progress modal */}
                <Modal isOpen={isImporting} backdrop="static" keyboard={false}>
                    <ModalHeader>{t('productsTabs.importing')}</ModalHeader>
                    <ModalBody>
                        <Progress value={progressPercent} className="mb-3">{progressPercent}%</Progress>
                        <p className="text-muted mb-0">{importProgress.message}</p>
                    </ModalBody>
                </Modal>

                <div className="page-content">
                    {
                        isReceiving || isUpdating || isDeleting ? (<Loader size={80}/>) : (<div></div>)
                    }
                    <TransitionGroup>
                        <CSSTransition
                        key={this.props.location.key}
                        classNames="fade"
                        timeout={600}
                        >
                        <Switch>
                            <Route path="/admin/products" exact render={() => <Redirect to="/admin/products/categories" />} />
                            <Route path="/admin/products/prod_list" exact component={ProdList} />
                            <Route path="/admin/products/prod_list/:id" exact component={ProductEdit} />
                            <Route path="/admin/products/prod_list/create" exact component={ProductEdit} />
                            <Route path="/admin/products/categories" exact component={Categories} />
                            <Route path="/admin/products/menus/:menuId" exact component={MenuDetail} />
                            <Route path="/admin/products/menus" exact component={Menus} />
                        </Switch>
                        </CSSTransition>
                    </TransitionGroup>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        products: state.products.data,
        categories: state.products.categories,
        merchant: state.auth.merchant,
        isReceiving: state.products.isReceiving || state.menus.isReceiving,
        isUpdating: state.products.isUpdating || state.menus.isUpdating,
        isDeleting: state.products.isDeleting,
        isFetching: state.auth.isFetching,
        errorMessage: state.auth.errorMessage,
        catalogError: state.products.error,
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(Products)));

